import React from 'react'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material/styles'
import { Box, Button } from '@mui/material'
import NotificationsIcon from '@mui/icons-material/Notifications'
import HelpIcon from '@mui/icons-material/Help'
import { ReactComponent as YqLogo } from 'ui/images/YQ_Logo.svg'
import IconPopOver from 'ui/utils/IconPopOver'
import { H1 } from 'ui/shorthands/Texts'
import { UserSupportPopOver } from './PopOverContent'
import { appBarHeight } from 'ui/theme'
import { InformationToMed } from 'types/med'

const useStyles = makeStyles()((theme: Theme) => ({
  appBar: {
    height: appBarHeight,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
    width: '100%',
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
    display: 'flex',
    placeContent: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
  },
  logo: {
    verticalAlign: 'middle',
  },
  help: {
    verticalAlign: 'middle',
  },
  h1: {
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.pxToRem(16),
    },
  },
  updateLink: {
    fontWeight: 400,
    fontSize: 14,
    marginRight: theme.spacing(2),
    textTransform: 'none',
  },
  updateIcon: {
    '&:first-child': {
      fontSize: 24,
    },
  },
}))

const AppBar: React.FC<{
  logo?: boolean
  children?: any
}> = ({ children, logo = false }) => {
  const { classes } = useStyles()
  return (
    <div className={classes.appBar}>
      {logo && <YqLogo className={classes.logo} title="YaDoc Quick" />}
      {children}
    </div>
  )
}

/**
 * ログイン画面、エラー画面などで使う受診者、医療機関に対応したH1をchildrenに持つAppBar
 * @param children
 * @param type
 * @constructor
 */
export const AppBarCommon: React.FC<{
  children?: any
  type?: 'med' | 'personal'
}> = ({ children, type = 'med' }) => {
  const { classes } = useStyles()
  return (
    <AppBar logo>
      <H1 className={classes.h1}>{children}</H1>
      <IconPopOver
        content={<UserSupportPopOver type={type} />}
        icon={<HelpIcon className={classes.help} color="primary" />}
      />
    </AppBar>
  )
}

/**
 * 施設側管理画面で使用
 * @constructor
 */
export const AppBarAdmin: React.FC<{ update?: InformationToMed }> = ({
  update,
}) => {
  const { classes } = useStyles()

  return (
    <AppBar logo>
      <div>
        {update?.title && (
          <>
            <Button
              color={'primary'}
              component={'a'}
              href={update.url}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<NotificationsIcon className={classes.updateIcon} />}
              className={classes.updateLink}
            >
              {update.title}
            </Button>
          </>
        )}
        <IconPopOver
          content={<UserSupportPopOver type={'med'} />}
          icon={<HelpIcon className={classes.help} color="primary" />}
        />
      </div>
    </AppBar>
  )
}

export const AppBarPatient: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const { classes } = useStyles()
  return (
    <AppBar>
      <Box width="1em" />
      <H1 className={classes.h1}>{children}</H1>
      <IconPopOver
        content={<UserSupportPopOver type="personal" />}
        icon={<HelpIcon className={classes.help} color="primary" />}
      />
    </AppBar>
  )
}

export const AppBarIhAdmin: React.FC<{
  children?: React.ReactNode
  util?: React.ReactNode
}> = ({ children, util }) => {
  const { classes } = useStyles()
  return (
    <AppBar logo>
      <H1 className={classes.h1}>{children}</H1>
      <div>{util}</div>
    </AppBar>
  )
}
